import React, { useContext, PropsWithChildren } from 'react';
import { AuthContext } from '../context/AuthContext';
import { UserDataContext } from '../context/UserDataContext';
import { NotificationsContext } from '../context/NotificationsContext';
import { FollowsContext } from '../context/FollowsContext';
import { User } from '@supabase/supabase-js';
import { UserRecord } from '../model/types';

// Create a backward compatibility context that matches the old MyContext interface
export interface MyContext {
  user: User | null;
  userRecord: UserRecord | null;
  isLoading: boolean;
  authError: string | null;
  isEmailVerified: boolean;
  getNotifications: () => any[] | null;
  getFollows: () => any[] | null;
  clearAuthError: () => void;
}

export const CompatibilityContext = React.createContext<MyContext>({
  user: null,
  userRecord: null,
  isLoading: true,
  authError: null,
  isEmailVerified: false,
  getNotifications: () => [],
  getFollows: () => [],
  clearAuthError: () => {},
});

/**
 * This provider combines all our separate providers and exposes their data
 * through a single context that matches the old MyContext interface.
 *
 * This is temporary for backward compatibility while we migrate components
 * to use the separate contexts.
 */
export const CompatibilityProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const auth = useContext(AuthContext);
  const userData = useContext(UserDataContext);
  const notifications = useContext(NotificationsContext);
  const follows = useContext(FollowsContext);

  // Combine all contexts into a single context matching the old interface
  const compatibilityValue: MyContext = {
    user: auth.user,
    userRecord: userData.userRecord,
    isLoading: auth.isLoading || userData.isLoading,
    authError: auth.authError,
    isEmailVerified: auth.isEmailVerified,
    getNotifications: notifications.getNotifications,
    getFollows: follows.getFollows,
    clearAuthError: auth.clearAuthError,
  };

  return (
    <CompatibilityContext.Provider value={compatibilityValue}>
      {children}
    </CompatibilityContext.Provider>
  );
};
